<template>
  <div class="plan-details">
    <div class="plan-details__header">
      <Passes
        class="plan-details__icon"
        :passCode="planCode"
        :passName="planName"
        :size="50"
      />
      <p class="plan-details__title">{{ planName }} Subscription</p>
    </div>
    <div class="plan-details__section" v-if="renewal">
      <p class="plan-details__section--name" id="expiry">Next billing amount</p>
      <p class="plan-details__section--data">
        {{ renewal.amount }}
      </p>
      <p class="plan-details__section--detail">on {{ renewalDate }}</p>
    </div>
    <div class="plan-details__section" v-if="cancelledSection">
      <p
        v-if="!isPlanPending"
        class="plan-details__section--name"
        id="cancelled"
      >
        Cancelled
      </p>
      <p v-else class="plan-details__section--name" id="pending">Pending</p>
    </div>
    <template v-if="!isBusinessSubscription">
      <div class="plan-details__section">
        <p class="plan-details__section--name">
          {{ isPlanRenewable ? 'Renews on' : 'Expires on' }}
        </p>
        <p class="plan-details__section--data">
          {{ planExpiryDate }}
          <AlertTriangleIcon
            v-if="!isPlanActive && !isPlanFailed && !isPlanPending"
            class="plan-details__section--icon"
          />
        </p>
        <p class="plan-details__section--detail">
          {{ daysRemainingForSub }} Days remaining
        </p>
      </div>
      <div v-if="planHasProvider" id="credit" class="plan-details__section">
        <p class="plan-details__section--name">Available Credit</p>
        <p class="plan-details__section--data">{{ accountCredit }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import Passes from '@/components/properties/Passes.vue'
import AlertTriangleIcon from '@/assets/icons/subscriptions/alert-triangle.svg'
import { formatSubDate } from '@/helpers'

export default {
  name: 'PlanDetails',
  components: {
    Passes,
    AlertTriangleIcon: { ...AlertTriangleIcon, name: 'AlertTriangleIcon' },
  },
  props: {
    planName: {
      type: String,
      required: true,
    },
    planCode: {
      type: String,
      required: true,
    },
    accountCredit: {
      type: String,
      required: true,
    },
    renewal: {
      type: Object,
      required: false,
    },
    isBusinessSubscription: {
      type: Boolean,
      required: true,
    },
    isPlanActive: {
      type: Boolean,
      required: true,
    },
    isPlanFailed: {
      type: Boolean,
      required: true,
    },
    isPlanInCancelledState: {
      type: Boolean,
      required: true,
    },
    isPlanPending: {
      type: Boolean,
      required: true,
    },
    planExpiryDate: {
      type: String,
      required: true,
    },
    daysRemainingForSub: {
      type: Number,
      required: true,
    },
    planHasProvider: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    renewalDate() {
      const renewal = this.renewal

      if (renewal && renewal.due_at) {
        return formatSubDate(renewal.due_at)
      }

      return null
    },
    cancelledSection() {
      return !this.isPlanActive && !this.isPlanFailed && this.planHasProvider
    },
    isPlanRenewable() {
      return this.isPlanActive && this.planHasProvider
    },
  },
}
</script>

<style lang="scss">
.plan-details {
  display: flex;
  flex-direction: column;
  filter: drop-shadow(0px 0px 20px rgba(56, 71, 109, 0.06));
  padding: 20px;
  justify-content: center;
  border-radius: 5px 5px 0 0;
  @include mobile {
    flex-direction: row;
    padding: 25px;
    justify-content: flex-start;
    align-items: stretch;
  }
  &__header {
    display: flex;
    justify-content: center;
    flex-direction: column;
    @include mobile {
      max-height: 50px;
      flex-direction: row;
      align-self: flex-start;
    }
  }
  &__title {
    font-size: rem(18);
    line-height: 24px;
    color: var(--white);
    align-self: center;
    @include mobile {
      margin: 0 10px 0 20px;
    }
  }
  &__icon {
    width: 50px;
    margin-bottom: 20px;
    align-self: center;
    @include mobile {
      margin: 0;
    }
  }
  &__section {
    display: flex;
    flex-direction: column;
    text-align: center;
    &:not(:last-child) {
      padding-bottom: 10px;
      border-bottom: 1px solid var(--tundora-gray);
      margin: 0 0 10px 0;
    }
    @include mobile {
      &:not(:last-child) {
        padding: 0 20px 0 0;
        border-right: 1px solid var(--tundora-gray);
        border-bottom: none;
        margin: 0 20px 0 0;
      }
      &:nth-child(2) {
        margin-left: auto;
      }
    }
    &--title {
      color: var(--bright-gray);
      text-transform: uppercase;
      font-size: rem(14px);
      padding: 0;
      margin: 3px 0;
    }
    &--name {
      font-size: rem(12px);
      line-height: 14px;
      color: var(--chetwode-blue);
      margin: 0;
    }
    &--data {
      font-size: rem(16);
      line-height: 23px;
      color: var(--white);
      margin: 0;
    }
    &--detail {
      font-size: rem(12);
      line-height: 14px;
      color: var(--light-blue-gray);
      margin: 0;
    }
    &--pending {
      color: var(--orange);
      text-transform: uppercase;
      margin: 0;
      font-family: var(--font-bold);
      font-size: rem(16px);
    }
    &--icon {
      fill: var(--orange);
      width: 20px;
      height: 18px;
      margin: 0 0 0 5px;
      display: none;
      @include mobile {
        display: inline;
      }
    }
  }
}
</style>
